import React, { useState, useEffect, useMemo } from "react";
import { ResponsivePie } from "@nivo/pie";
import PropTypes from "prop-types";
import { useSearchParams, useLocation } from "react-router-dom";
import { 
  fetchInviteClaraResponseCategorization, 
  fetchWtwClaraResponseCategorization,
  fetchClaraResponseCategorizationForIframe
} from "../services/api";
import { 
  ENV_TYPE_PROD,
  CATEGORY_TYPE_CLARA_RESPONSE,
  CATEGORY_TYPE_USER_QUERY
} from "../constants";

/**
 * Custom hook to detect screen size for responsive chart rendering
 * @param {number} breakpoint - Width threshold for mobile/desktop (default: 768px)
 * @returns {boolean} True if screen is mobile-sized
 */
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

/**
 * Custom hook to detect ultra-wide screens (aspect ratio > 2.2)
 * @returns {boolean} True if screen is ultra-wide
 */
const useIsUltraWide = () => {
  const [isUltraWide, setIsUltraWide] = useState(
    typeof window !== 'undefined' ? window.innerWidth / window.innerHeight > 2.2 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsUltraWide(window.innerWidth / window.innerHeight > 2.2);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isUltraWide;
};

/**
 * Intelligently shorten category labels for mobile display
 * @param {string} label - Full category label
 * @returns {string} Shortened label
 */
const shortenLabel = (label) => {
  const shortMappings = {
    'Clarification & Understanding': 'Clarification',
    'Reflection & Introspection': 'Reflection',
    'Social Conversations': 'Social',
    'Imagery & Detail': 'Imagery',
    'Research & Factual Information': 'Research',
    'Grammar, Mechanics, & Formatting': 'Grammar',
    'Characterization & Relationships': 'Characters',
    'Brainstorming & Inspiration': 'Brainstorming',
    'Writing Structure & Organization': 'Structure',
    'Revision & Editing': 'Revision',
    'Plot Development & Pacing': 'Plot',
    'Dialogue & Voice': 'Dialogue',
    'World Building': 'World Building',
    'Theme & Symbolism': 'Theme',
    'Creative Exercises': 'Exercises',
    'Technical Writing': 'Technical',
    'Poetry & Verse': 'Poetry',
    'Genre-Specific': 'Genre',
    'Publishing & Career': 'Publishing',
    'Other': 'Other'
  };
  
  return shortMappings[label] || (label.length > 15 ? label.substring(0, 15) + '...' : label);
};

/**
 * Generate distinct colors using HSL color space
 * Distributes colors evenly around the color wheel for maximum visual distinction
 * @param {number} count - Number of colors to generate
 * @returns {string[]} Array of color strings in HSL format
 */
const generateDistinctColors = (count) => {
  const colors = [];
  const goldenRatioConjugate = 0.618033988749895; // Use golden ratio for better distribution
  let hue = Math.random(); // Start with random hue for variety
  
  for (let i = 0; i < count; i++) {
    // Rotate hue using golden ratio for optimal spacing
    hue += goldenRatioConjugate;
    hue %= 1;
    
    // Convert to degrees (0-360)
    const h = Math.floor(hue * 360);
    
    // Vary saturation and lightness for additional distinction
    // Higher saturation for vibrant colors
    const s = 65 + (i % 3) * 10; // 65%, 75%, 85%
    // Lightness in readable range
    const l = 50 + (i % 4) * 5; // 50%, 55%, 60%, 65%
    
    colors.push(`hsl(${h}, ${s}%, ${l}%)`);
  }
  
  return colors;
};

const ClaraFeedbackBreakdown = ({ id: propId, source: propSource, envType: propEnvType }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Check if this is an iframe route (no login required)
  const isIframeRoute = location.pathname.includes('/iframe/');
  
  // Get parameters from URL params, fallback to props (no default id - empty means all)
  const id = searchParams.get('id') || propId || null;
  const source = searchParams.get('source') || propSource || "invite-clara";
  const darkMode = searchParams.get('darkMode') === 'true';
  
  const envType = propEnvType ?? ENV_TYPE_PROD;
  
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryType, setCategoryType] = useState(CATEGORY_TYPE_USER_QUERY);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Detect if device is mobile for responsive chart rendering
  const isMobile = useIsMobile();
  
  // Detect if screen is ultra-wide for horizontal layout
  const isUltraWide = useIsUltraWide();

  // Generate dynamic colors based on the number of data points
  const chartColors = useMemo(() => {
    if (!data || data.length === 0) return [];
    return generateDistinctColors(data.length);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      if (!source) {
        setError("Missing required parameter: source is required");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        let response;
        
        // Use iframe endpoint for iframe routes (origin-validated, no login)
        if (isIframeRoute) {
          response = await fetchClaraResponseCategorizationForIframe(id, source, envType, categoryType);
        } 
        // Use authenticated endpoints for normal routes (requires login)
        else {
          if (source === "invite-clara") {
            response = await fetchInviteClaraResponseCategorization(id);
          } else if (source === "wtw-clara") {
            response = await fetchWtwClaraResponseCategorization(id);
          } else {
            throw new Error(`Invalid source: ${source}. Must be 'invite-clara' or 'wtw-clara'`);
          }
        }

        if (response.success) {
          // Determine which field to use based on category type
          const categoryField = categoryType === CATEGORY_TYPE_USER_QUERY 
            ? 'user_query_category' 
            : 'clara_response_category';
          
          // Transform API response to chart data format
          const processedData = response.data.map((item) => ({
            id: item[categoryField],
            value: item.count,
            label: item[categoryField]
          }));
          setData(processedData);
        } else {
          throw new Error(response.error || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching Clara data:", err);
        
       
        if (err.response?.status === 403) {
          setError("Access forbidden. This iframe can only be embedded on authorized domains.");
        } else {
          setError(err.message || "Failed to fetch data");
        }
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [id, source, envType, isIframeRoute, categoryType]);

  // Show initial loading screen only on first load
  if (isInitialLoad && isLoading) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <span className={`text-xl font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Checking Authentication...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`rounded-lg shadow-lg p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>Error</h1>
          <p className={`text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Access Denied</p>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            You do not have the required permissions to view this content.
          </p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center min-h-screen w-full z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`rounded-lg shadow-lg p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h1 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            Category Breakdown
          </h1>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>No data available for the specified parameters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto">
        <div className={`rounded-lg shadow-lg p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={isUltraWide ? 'flex items-center gap-6' : ''}>
            {/* Title and Toggle Section */}
            {isUltraWide ? (
              <div className="flex-shrink-0 flex items-center justify-center" style={{ width: '120px', height: '600px' }}>
                <div style={{ 
                  transform: 'rotate(-90deg)', 
                  transformOrigin: 'center center',
                  width: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <h1 className={`text-3xl font-bold mb-4 text-center whitespace-nowrap ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    Clara Category Breakdown
                  </h1>
                  
                  {/* Category Type Toggle - Only show for iframe routes */}
                  {isIframeRoute && (
                    <div className="flex justify-center mb-6">
                      <div className="inline-flex rounded-lg border p-1" style={{
                        backgroundColor: darkMode ? '#374151' : '#ffffff',
                        borderColor: darkMode ? '#4b5563' : '#d1d5db'
                      }}>
                        <button
                          onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                          disabled={isLoading}
                          className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_USER_QUERY
                              ? "bg-blue-600 text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          User Query Category
                        </button>
                        <button
                          onClick={() => setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)}
                          disabled={isLoading}
                          className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_CLARA_RESPONSE
                              ? "bg-blue-600 text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          Clara Response Category
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <h1 className={`text-3xl font-bold mb-4 text-center ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Clara Category Breakdown
                </h1>
                
                {/* Category Type Toggle - Only show for iframe routes */}
                {isIframeRoute && (
                  <div className="flex justify-center mb-6">
                    <div className={`inline-flex rounded-lg border p-1 ${
                      darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'
                    }`}>
                      
                      <button
                        onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                        disabled={isLoading}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                          categoryType === CATEGORY_TYPE_USER_QUERY
                            ? "bg-blue-600 text-white shadow-sm"
                            : darkMode
                              ? "text-gray-300 hover:bg-gray-600"
                              : "text-gray-700 hover:bg-gray-100"
                        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        User Query Category
                      </button>
                      <button
                        onClick={() => setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)}
                        disabled={isLoading}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                          categoryType === CATEGORY_TYPE_CLARA_RESPONSE
                            ? "bg-blue-600 text-white shadow-sm"
                            : darkMode
                              ? "text-gray-300 hover:bg-gray-600"
                              : "text-gray-700 hover:bg-gray-100"
                        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        Clara Response Category
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Chart Section */}
            <div className={`flex justify-center relative ${isUltraWide ? 'flex-1' : ''}`}>
            {/* Loading overlay - only show during category toggle */}
            {!isInitialLoad && isLoading && (
              <div className={`absolute inset-0 bg-opacity-75 flex items-center justify-center z-10 rounded-lg ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              }`}>
                <div className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Updating chart...</div>
              </div>
            )}
            
            {/* Render custom HTML bar chart on mobile, pie chart on desktop */}
            {isMobile ? (
              <div className="w-full px-2 py-4">
                {/* Legend/Title */}
                <div className={`text-center mb-6 text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Tap any bar to see full category name
                </div>
                
                {/* Custom HTML Bar Chart */}
                <div className="space-y-3">
                  {data
                    .sort((a, b) => b.value - a.value) // Sort by value descending
                    .map((item, index) => {
                      const maxValue = Math.max(...data.map(d => d.value));
                      const percentage = (item.value / maxValue) * 100;
                      const shortLabel = shortenLabel(item.label);
                      
                      return (
                        <div 
                          key={item.id}
                          className={`group relative ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} p-2 rounded-lg transition-all cursor-pointer`}
                          title={item.label} // Native tooltip for accessibility
                        >
                          {/* Label and Value Row */}
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                              {shortLabel}
                            </span>
                            <span className={`text-sm font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'} ml-2`}>
                              {item.value}
                            </span>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className={`w-full h-8 rounded-lg overflow-hidden ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}>
                            <div
                              className="h-full flex items-center justify-end px-3 transition-all duration-500 ease-out"
                              style={{
                                width: `${Math.max(percentage, 5)}%`, // Minimum 5% width for visibility
                                backgroundColor: chartColors[index],
                              }}
                            >
                              <span className="text-xs font-semibold text-white drop-shadow-md">
                                {item.value}
                              </span>
                            </div>
                          </div>
                          
                          {/* Hover/Touch Tooltip - Shows full label */}
                          {shortLabel !== item.label && (
                            <div className={`
                              absolute left-0 right-0 top-full mt-1 p-3 rounded-lg shadow-2xl border z-10
                              opacity-0 group-hover:opacity-100 group-active:opacity-100
                              pointer-events-none transition-opacity duration-200
                              ${darkMode 
                                ? 'bg-gray-800 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-800'
                              }
                            `}>
                              <div className="text-sm font-semibold">{item.label}</div>
                              <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Count: {item.value}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
                
                {/* Total Count Summary */}
                <div className={`mt-6 pt-4 border-t text-center ${
                  darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'
                }`}>
                  <span className="text-sm font-medium">Total Count: </span>
                  <span className="text-lg font-bold">
                    {data.reduce((sum, item) => sum + item.value, 0)}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ height: "600px", width: "100%", minWidth: "300px" }}>
                <ResponsivePie
                  key={`${id}-${source}-${categoryType}-pie`}
                  data={data}
                  margin={{ top: 60, right: 180, bottom: 60, left: 180 }}
                  innerRadius={0.4}
                  padAngle={2}
                  cornerRadius={4}
                  colors={chartColors}
                  borderWidth={1}
                  borderColor={darkMode ? "rgba(31, 41, 55, 0.8)" : "rgba(255, 255, 255, 0.8)"}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
                  
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor={darkMode ? "#e5e7eb" : "#333333"}
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: "color" }}
                  arcLinkLabelsDiagonalLength={16}
                  arcLinkLabelsStraightLength={24}
                  arcLinkLabelsOffset={0}
                  enableArcLabels={true}
                  arcLabelsRadiusOffset={0.6}
                  theme={{
                    labels: {
                      text: {
                        fontSize: 14,
                        fontWeight: 600,
                        fill: darkMode ? "#e5e7eb" : "#333333"
                      }
                    },
                    tooltip: {
                      container: {
                        background: darkMode ? "#1f2937" : "#ffffff",
                        color: darkMode ? "#e5e7eb" : "#333333"
                      }
                    }
                  }}
                  tooltip={({ datum }) => (
                    <div className={`p-4 rounded-xl shadow-xl border backdrop-blur-sm ${
                      darkMode 
                        ? 'bg-gray-800 border-gray-600' 
                        : 'bg-white border-gray-200'
                    }`}>
                    <div className={`font-bold text-lg mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{datum.label}</div>
                    <div className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Count: {datum.value}</div>
                  </div>
                  )}
                  animate={true}
                  motionConfig="gentle"
                />
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ClaraFeedbackBreakdown.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  source: PropTypes.oneOf(["invite-clara", "wtw-clara"]),
  envType: PropTypes.oneOf([0, 1, 2]), // 0 = test, 1 = production, 2 = dev
};

export default ClaraFeedbackBreakdown;
